import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FeatureFlagDevToolsUI } from '@liveauctioneers/hammer-ui-core/featureFlags/devTools/FeatureFlagDevToolsUI';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchHealthCheckData, getHealthCheckData, getHealthCheckLoading } from '@/redux/modules/healthcheck';

import { AmplitudeExperimentFlags } from './components/AmplitudeExperimentFlags/AmplitudeExperimentFlags';
import { ApiPerformanceStats } from './components/ApiPerformanceStats/ApiPerformanceStats';
import { ReduxStoreSizeStats } from './components/ReduxStoreSizeStats/ReduxStoreSizeStats';

export const HealthCheckOverlay = () => {
    const [show, setShow] = useState(false);
    const dispatch = useAppDispatch();
    const healthCheckData = useAppSelector(getHealthCheckData);
    const healthCheckLoading = useAppSelector(getHealthCheckLoading);

    const handleListenForShortcut = (e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && (e.key === '`' || e.key === '/')) {
            setShow(true);
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleListenForShortcut);
        return () => {
            window.removeEventListener('keydown', handleListenForShortcut);
        };
    }, []);

    useEffect(() => {
        if (show) {
            dispatch(fetchHealthCheckData());
        }
    }, [dispatch, show]);

    return (
        show && (
            // z-[10400] is from a comment that this is our modal z-index
            <section className="fixed left-0 top-0 z-[10400] h-full w-full overflow-auto overscroll-contain bg-background-primary">
                {/* There are too many nested a and ul to make use of more specific styling, thus &a and &ul */}
                <div className="[&_ul]:bg-background-primary! relative p-2xl text-left [&_a]:!pl-0">
                    <IconButton
                        className="absolute right-lg top-lg"
                        data-testid="health-check-overlay-close"
                        icon={Close}
                        onClick={() => setShow(false)}
                    />

                    <ReduxStoreSizeStats />
                    <FeatureFlagDevToolsUI />
                    <AmplitudeExperimentFlags />
                    <ApiPerformanceStats className="mb-2xl" />
                    <H2>
                        <FormattedMessage id="healthcheck.sectionTitle.deploymentData" />
                        <Button
                            aria-label="fetch health check data"
                            className="ml-sm"
                            loading={healthCheckLoading}
                            onClick={() => {
                                dispatch(fetchHealthCheckData());
                            }}
                            size="sm"
                        >
                            <FormattedMessage id="healthcheck.refresh" />
                        </Button>
                    </H2>
                    <ul>
                        <li>
                            <BodyPrimary>Build Number: {healthCheckData.application.build_number || 'N/A'}</BodyPrimary>
                        </li>
                        <li>
                            <BodyPrimary>Deployment: {healthCheckData.application.deployment}</BodyPrimary>
                        </li>
                        <li>
                            <BodyPrimary>Name: {healthCheckData.application.name || 'Local Development'}</BodyPrimary>
                        </li>
                        <li>
                            <BodyPrimary>Server Status: {healthCheckData.server.status}</BodyPrimary>
                        </li>
                        <li>
                            <BodyPrimary>Server Uptime: {healthCheckData.server.uptime}</BodyPrimary>
                        </li>
                        <li>
                            <BodyPrimary>Memory Available: {healthCheckData.server.memory.total_available}</BodyPrimary>
                        </li>
                        <li>
                            <BodyPrimary>Memory Free: {healthCheckData.server.memory.total_free}</BodyPrimary>
                        </li>
                    </ul>
                </div>
            </section>
        )
    );
};
